const API = "/api/development";

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[2]) : null;
}

async function api(path, opts = {}) {
  const isForm = opts.isForm || (typeof FormData !== "undefined" && opts.body instanceof FormData);
  const headers = { ...(opts.headers || {}) };
  if (!isForm) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.method && opts.method !== "GET") {
    headers["X-CSRFToken"] = getCookie("csrftoken");
  }
  const res = await fetch(API + path, {
    credentials: "include",
    ...opts,
    headers,
    body: opts.body ? (isForm ? opts.body : JSON.stringify(opts.body)) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = (await res.json()).detail || detail;
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  return res.status === 204 ? null : res.json();
}

export const getPrograms = () => api("/programs/");
export const getProgram = (id) => api(`/programs/${id}/`);
export const createProgram = (data) =>
  api("/programs/", { method: "POST", body: data });
export const setProgramStatus = (id, flag, comment) =>
  api(`/programs/${id}/set_status/`, { method: "POST", body: { flag, comment } });
export const setProgramBackground = (id, field, value) =>
  api(`/programs/${id}/set_background/`, { method: "POST", body: { field, value } });

// Task completion (open to anyone — no justification; keys stay ⊆ {done}).
export const toggleTaskDone = (id, done) =>
  api(`/tasks/${id}/`, { method: "PATCH", body: { done } });
// Task create/content-edit/delete (restricted — carry justification).
export const createTask = (sprint, text, order, justification) =>
  api("/tasks/", { method: "POST", body: { sprint, text, order, justification } });
export const updateTaskText = (id, text, justification) =>
  api(`/tasks/${id}/`, { method: "PATCH", body: { text, justification } });
export const deleteTask = (id, justification) =>
  api(`/tasks/${id}/`, { method: "DELETE", body: { justification } });

// Sprint edit (restricted — objective/dates carry justification).
export const updateSprint = (id, data, justification) =>
  api(`/sprints/${id}/`, { method: "PATCH", body: { ...data, justification } });
export const getSprintReview = () => api("/sprints/review/");

// Gate criteria (restricted, per-bullet) & approvers (restricted CRUD).
export const editGateCriteria = (gateId, list, op, payload, justification) =>
  api(`/gates/${gateId}/edit_criteria/`, {
    method: "POST",
    body: { list, op, ...payload, justification },
  });
export const createApprover = (data, justification) =>
  api("/gate-approvers/", { method: "POST", body: { ...data, justification } });
export const updateApprover = (id, data, justification) =>
  api(`/gate-approvers/${id}/`, { method: "PATCH", body: { ...data, justification } });
export const deleteApprover = (id, justification) =>
  api(`/gate-approvers/${id}/`, { method: "DELETE", body: { justification } });

export const signGate = (id, payload) =>
  api(`/gates/${id}/sign/`, { method: "POST", body: payload });

// Restricted edits — every write carries a justification (ISO change log).
export const updateRisk = (id, data, justification) =>
  api(`/risks/${id}/`, { method: "PATCH", body: { ...data, justification } });
export const createRisk = (data, justification) =>
  api("/risks/", { method: "POST", body: { ...data, justification } });
export const deleteRisk = (id, justification) =>
  api(`/risks/${id}/`, { method: "DELETE", body: { justification } });

export const getChangeLogs = (programId) =>
  api(`/programs/${programId}/change_logs/`);

// Completion criteria (restricted)
export const updateCriterion = (id, data, justification) =>
  api(`/criteria/${id}/`, { method: "PATCH", body: { ...data, justification } });
export const createCriterion = (data, justification) =>
  api("/criteria/", { method: "POST", body: { ...data, justification } });
export const deleteCriterion = (id, justification) =>
  api(`/criteria/${id}/`, { method: "DELETE", body: { justification } });

// Phase goals (restricted, per-bullet)
export const editPhaseGoals = (phaseId, op, payload, justification) =>
  api(`/phases/${phaseId}/edit_goals/`, {
    method: "POST",
    body: { op, ...payload, justification },
  });

// Phase edit — name (free) and/or start/end duration (justification required
// when a date changes). Send any subset of {name, start, end}.
export const editPhase = (phaseId, data, justification) =>
  api(`/phases/${phaseId}/edit_phase/`, {
    method: "POST",
    body: { ...data, justification },
  });

// Program lead / owner assignment (EM-only on the backend).
export const getAssignableUsers = () => api("/assignable-users/");
export const setProgramLead = (programId, userId) =>
  api(`/programs/${programId}/set_lead/`, {
    method: "POST",
    body: { user_id: userId },
  });

// ── Round 5: costs ──────────────────────────────────────────────────────────
const costCrud = (base) => ({
  list: (programId) => api(`/${base}/?program=${programId}`),
  create: (data, justification) => api(`/${base}/`, { method: "POST", body: { ...data, justification } }),
  update: (id, data, justification) => api(`/${base}/${id}/`, { method: "PATCH", body: { ...data, justification } }),
  remove: (id, justification) => api(`/${base}/${id}/`, { method: "DELETE", body: { justification } }),
});
export const materialPurchases = costCrud("material-purchases");
export const inventoryConsumption = costCrud("inventory-consumption");
export const machineHours = costCrud("machine-hours");
export const externalServices = costCrud("external-services");
export const setCostSummary = (programId, value, justification) =>
  api(`/programs/${programId}/set_cost_summary/`, { method: "POST", body: { value, justification } });

// ── Round 5: documents ──────────────────────────────────────────────────────
export const listDocuments = (programId, section) =>
  api(`/documents/?program=${programId}&section=${section}`);
export const uploadDocument = (programId, section, file, description) => {
  const fd = new FormData();
  fd.append("program", programId);
  fd.append("section", section);
  fd.append("file", file);
  if (description) fd.append("description", description);
  // Multipart: let the browser set the Content-Type/boundary; api() must not
  // force application/json for FormData bodies.
  return api("/documents/", { method: "POST", body: fd, isForm: true });
};
export const deleteDocument = (id) => api(`/documents/${id}/`, { method: "DELETE" });
